import { useEffect, useState } from 'react'
import { Box, Card, Divider, Stack, Typography, useMediaQuery } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import {
  BoxAge,
  CardFiltration,
  DividerStyled,
  FiltrationAutocomplete,
  FiltrationText,
  StackAge,
} from './style'
import { FormBlock } from '../../../common/FormGenerator'
import { getFormConfig } from './settingsConfig'
import { PureLightTheme } from '../../../styles/theme'
import { TEXT_FILTER_DEBOUNCE_MS } from '../../../consts/filtration'

const normalizeStringFilter = (value: unknown): string | undefined => {
  const normalized = String(value ?? '').trim()
  if (!normalized || normalized === 'all') {
    return undefined
  }
  return normalized
}

const normalizeAgeFilter = (value: unknown): number | undefined => {
  const normalized = Number(value)
  if (!Number.isFinite(normalized) || normalized <= 0) {
    return undefined
  }
  return normalized
}

function urlSearchParamsEqual(a: URLSearchParams, b: URLSearchParams): boolean {
  const keys = new Set<string>()
  a.forEach((_, key) => keys.add(key))
  b.forEach((_, key) => keys.add(key))
  for (const key of Array.from(keys)) {
    if (a.get(key) !== b.get(key)) return false
  }
  return true
}

export const UsersFiltration = () => {
  const isTablet = useMediaQuery(PureLightTheme.breakpoints.down('md'))
  const { watch } = useFormContext()
  const [, setSearchParams] = useSearchParams()
  const query = watch('query')
  const city = watch('city')
  const status = watch('status')
  const ageFrom = watch('age_from')
  const ageTo = watch('age_to')
  const [debouncedQuery, setDebouncedQuery] = useState(query)
  const [debouncedCity, setDebouncedCity] = useState(city)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query)
    }, TEXT_FILTER_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [query])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedCity(city)
    }, TEXT_FILTER_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [city])

  useEffect(() => {
    const normalizedQuery = normalizeStringFilter(debouncedQuery)
    const normalizedCity = normalizeStringFilter(debouncedCity)
    const normalizedStatus = normalizeStringFilter(status)
    let normalizedAgeFrom = normalizeAgeFilter(ageFrom)
    let normalizedAgeTo = normalizeAgeFilter(ageTo)

    if (normalizedAgeFrom !== undefined && normalizedAgeTo !== undefined && normalizedAgeFrom > normalizedAgeTo) {
      const tmp = normalizedAgeFrom
      normalizedAgeFrom = normalizedAgeTo
      normalizedAgeTo = tmp
    }

    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev)

        const syncParam = (key: string, value?: string) => {
          if (value) {
            nextParams.set(key, value)
          } else {
            nextParams.delete(key)
          }
        }

        syncParam('query', normalizedQuery)
        syncParam('city', normalizedCity)
        syncParam('status', normalizedStatus)
        syncParam('age_from', normalizedAgeFrom !== undefined ? String(normalizedAgeFrom) : undefined)
        syncParam('age_to', normalizedAgeTo !== undefined ? String(normalizedAgeTo) : undefined)

        return urlSearchParamsEqual(nextParams, prev) ? prev : nextParams
      },
      { replace: true },
    )
  }, [ageFrom, ageTo, debouncedCity, debouncedQuery, setSearchParams, status])

  return (
    <Card variant='primaryCard' sx={CardFiltration}>
      {!isTablet && (
        <Box mb={{ md: 2.5 }}>
          <FormBlock formFieldConfig={getFormConfig('query')} />
        </Box>
      )}

      <Typography variant='h4' sx={FiltrationText}>Город:</Typography>
      <Box sx={FiltrationAutocomplete}>
        <FormBlock formFieldConfig={getFormConfig('city')} />
      </Box>

      <Typography variant='h4' sx={FiltrationText}>Возраст:</Typography>
      <Stack direction='row' sx={StackAge}>
        <Box sx={BoxAge}>
          <FormBlock formFieldConfig={getFormConfig('age_from')} />
        </Box>
        <Divider sx={DividerStyled} />
        <Box sx={BoxAge}>
          <FormBlock formFieldConfig={getFormConfig('age_to')} />
        </Box>
      </Stack>
      
      <Typography variant='h4' sx={FiltrationText}>Статус:</Typography>
      <Box sx={FiltrationAutocomplete}>
        <FormBlock formFieldConfig={getFormConfig('status')} />
      </Box>

      {/* TODO: добавить фильтрацию по компетенциям, доступным ресурсам и условиям участия в проектах */}
      {/* <Typography variant='h4' sx={FiltrationText}>Компетенции:</Typography>
      <Box sx={FiltrationAutocomplete}>
        <FormBlock formFieldConfig={getFormConfig('competencies')} />
      </Box>
      
      <Stack gap={2}>
        <Box>
          <Typography variant='h4' sx={FiltrationText}>Доступные ресурсы:</Typography>
          <FormBlock formFieldConfig={getFormConfig('availableResources')} />
        </Box>
        <Box>
          <Typography variant='h4' sx={FiltrationText}>Условия участия в проектах:</Typography>
          <FormBlock formFieldConfig={getFormConfig('conditionsOfParticipation')} />
        </Box>
      </Stack> */}
    </Card>
  )
}
